import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";

export type CustomerModalMode = "view" | "edit" | "add";

interface CustomerModalProps {
  isOpen: boolean;
  mode: CustomerModalMode;
  customer: any; 
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
  documentTypes?: any[]; 
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  isOpen,
  mode,
  customer,
  isSaving,
  onClose,
  onSubmit,
  documentTypes = [],
}) => {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (isOpen) {
      if (mode === "add") {
        setFormData({});
      } else if (customer) {
        setFormData({ ...customer });
      }
    } else {
      setFormData({});
    }
  }, [isOpen, customer, mode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    // Limpiar error del campo cuando se modifica
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};
    
    if (!formData.identityDocumentTypeId) {
      newErrors.identityDocumentTypeId = 'El tipo de identificación es requerido';
    }
    
    if (!formData.identificationNumber || formData.identificationNumber.trim() === '') {
      newErrors.identificationNumber = 'El número de identificación es requerido';
    }
    
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'La razón social / nombre es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const isView = mode === "view";
  const isAdd = mode === "add";
  
  let title = "Detalles del Cliente";
  if (mode === "edit") title = "Editar Cliente";
  if (mode === "add") title = "Agregar Cliente";

  return (
    <Modal isOpen={isOpen} toggle={isSaving ? undefined : onClose} centered size="lg">
      <ModalHeader toggle={isSaving ? undefined : onClose}>{title}</ModalHeader>
      <ModalBody>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="identityDocumentTypeId">Tipo de Identificación <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  name="identityDocumentTypeId"
                  id="identityDocumentTypeId"
                  value={formData.identityDocumentTypeId || ""}
                  onChange={handleChange}
                  disabled={isView}
                  className={errors.identityDocumentTypeId ? 'is-invalid' : ''}
                >
                  <option value="">Seleccione...</option>
                  {documentTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>
                      {type.name || type.description || type.code}
                    </option>
                  ))}
                </Input>
                {errors.identityDocumentTypeId && (
                  <div className="invalid-feedback">
                    {errors.identityDocumentTypeId}
                  </div>
                )}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="identificationNumber">Identificación <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="identificationNumber"
                  id="identificationNumber"
                  value={formData.identificationNumber || ""}
                  onChange={handleChange}
                  disabled={isView}
                  className={errors.identificationNumber ? 'is-invalid' : ''}
                />
                {errors.identificationNumber && (
                  <div className="invalid-feedback">
                    {errors.identificationNumber}
                  </div>
                )}
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <FormGroup>
                <Label for="name">Razón Social / Nombre <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name || ""}
                  onChange={handleChange}
                  disabled={isView}
                  className={errors.name ? 'is-invalid' : ''}
                />
                {errors.name && (
                  <div className="invalid-feedback">
                    {errors.name}
                  </div>
                )}
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="email">Correo electrónico</Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  disabled={isView}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="phone">Teléfono</Label>
                <Input
                  type="text"
                  name="phone"
                  id="phone"
                  value={formData.phone || ""}
                  onChange={handleChange}
                  disabled={isView}
                />
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <FormGroup>
                <Label for="address">Dirección</Label>
                <Input
                  type="textarea"
                  name="address"
                  id="address"
                  value={formData.address || ""}
                  onChange={handleChange}
                  disabled={isView}
                />
              </FormGroup>
            </div>
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSaving}>
          Cerrar
        </Button>
        {!isView && (
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <><Spinner size="sm" className="me-2" /> Guardando...</> : (isAdd ? "Crear Cliente" : "Guardar Cambios")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CustomerModal;
